import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getActiveJobsCount, getActiveClientsCount, getTotalRevenue, getWebshopOrdersCount } from '../../actions/dashboardActions';

const TimeComparisonFilter = ({ selectedPeriod, onChange, options, label }) => (
  <div className="flex items-center space-x-2 mb-4 flex-wrap gap-y-2">
    <span className="text-sm text-gray-300">{label}</span>
    <div className="flex flex-wrap gap-1">
      {options.map((option) => (
        <button
          key={option.value}
          className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
            selectedPeriod === option.value
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  </div>
);

const MetricSelector = ({ options, selectedMetric, onChange, label }) => (
  <div className="flex items-center space-x-2 mb-4 flex-wrap gap-y-2">
    <span className="text-sm text-gray-300">{label}</span>
    <div className="flex flex-wrap gap-1">
      {options.map((option) => (
        <button
          key={option.value}
          className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
            selectedMetric === option.value
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  </div>
);

const BarDisplay = ({ data, selectedMetric, formatValue, labels }) => {
  const maxValue = Math.max(1, ...data.map(item => Math.max(item.current, item.previous)));

  const getBarWidth = (value) => {
    if (!value) return 0;
    return Math.min(100, Math.max(0, (value / maxValue) * 100));
  };

  return (
    <div className="space-y-4">
      {data.map((item, index) => (
        <div key={index} className="mb-2">
          <div className="space-y-2">
            <div className="flex items-center">
              <div className="w-24 text-xs text-gray-400">{labels.current}</div>
              <div className="flex-1 bg-gray-700 h-5 rounded-md overflow-hidden">
                <div
                  className="bg-blue-600 h-full rounded-md"
                  style={{ width: `${getBarWidth(item.current)}%` }}
                ></div>
              </div>
              <div className="ml-2 text-xs font-medium text-blue-400">
                {formatValue(item.current, selectedMetric)}
              </div>
            </div>
            <div className="flex items-center">
              <div className="w-24 text-xs text-gray-400">{labels.previous}</div>
              <div className="flex-1 bg-gray-700 h-5 rounded-md overflow-hidden">
                <div
                  className="bg-gray-500 h-full rounded-md"
                  style={{ width: `${getBarWidth(item.previous)}%` }}
                ></div>
              </div>
              <div className="ml-2 text-xs font-medium text-gray-400">
                {formatValue(item.previous, selectedMetric)}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const ComparativeMetrics = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    metrics = {}
  } = useSelector(state => state.dashboard || {});

  const activeJobs = metrics.activeJobs || { current: 0, previous: 0, period: null };
  const activeClients = metrics.activeClients || { current: 0, previous: 0, period: null };
  const totalRevenue = metrics.totalRevenue || { current: 0, previous: 0, period: null };
  const webshopOrders = metrics.webshopOrders || { current: 0, previous: 0, period: null };

  const [selectedPeriod, setSelectedPeriod] = useState('week');
  const [selectedMetric, setSelectedMetric] = useState('activeJobs');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const metricOptions = useMemo(() => [
    { value: 'activeJobs', label: t('dashboard.metrics.activeJobs') },
    { value: 'activeClients', label: t('dashboard.metrics.activeClients') },
    { value: 'totalRevenue', label: t('dashboard.metrics.totalRevenue') },
    { value: 'webshopOrders', label: t('dashboard.metrics.webshopOrders') },
  ], [t]);

  const periodOptions = useMemo(() => [
    { value: 'day', label: t('dashboard.periods.day') },
    { value: 'week', label: t('dashboard.periods.week') },
    { value: 'month', label: t('dashboard.periods.month') },
    { value: 'year', label: t('dashboard.periods.year') },
  ], [t]);

  const formatNumber = useMemo(
    () => new Intl.NumberFormat('sr-RS'),
    []
  );

  const formatCurrency = useMemo(
    () => new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD', maximumFractionDigits: 0 }),
    []
  );

  const formatValue = (value, metric) => {
    if (metric === 'totalRevenue') {
      return formatCurrency.format(value || 0);
    }
    return formatNumber.format(value || 0);
  };

  const renderTrendIndicator = (trend) => {
    const formatted = Math.abs(trend).toFixed(1);

    if (trend > 0) {
      return (
        <div className="flex items-center text-green-300">
          <TrendingUp size={16} className="mr-1" />
          <span>{t('dashboard.comparative.trendUp', { value: formatted })}</span>
        </div>
      );
    }

    if (trend < 0) {
      return (
        <div className="flex items-center text-red-300">
          <TrendingDown size={16} className="mr-1" />
          <span>{t('dashboard.comparative.trendDown', { value: formatted })}</span>
        </div>
      );
    }

    return (
      <div className="flex items-center text-gray-400">
        <Minus size={16} className="mr-1" />
        <span>{t('dashboard.comparative.trendNeutral')}</span>
      </div>
    );
  };

  const currentMetricData = useMemo(() => {
    const data = {
      activeJobs,
      activeClients,
      totalRevenue,
      webshopOrders
    }[selectedMetric];
    
    // Only use data if it matches the selected period
    const dataMatchesPeriod = data && data.period === selectedPeriod;
    
    if (!dataMatchesPeriod) return null;
    
    return data;
  }, [activeJobs, activeClients, totalRevenue, webshopOrders, selectedMetric, selectedPeriod]);

  const chartData = useMemo(() => {
    if (!currentMetricData) return [];
    return [{
      current: currentMetricData.current || 0,
      previous: currentMetricData.previous || 0
    }];
  }, [currentMetricData]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        await Promise.all([
          dispatch(getActiveJobsCount(selectedPeriod)),
          dispatch(getActiveClientsCount(selectedPeriod)),
          dispatch(getTotalRevenue(selectedPeriod)),
          dispatch(getWebshopOrdersCount(selectedPeriod))
        ]);
      } catch (err) {
        setError(t('dashboard.comparative.errorTitle'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedPeriod, dispatch, t]);

  const calculateTrend = (current, previous) => {
    // Ensure we have valid numbers
    const currentNum = Number(current) || 0;
    const previousNum = Number(previous) || 0;
    
    // Handle edge cases
    if (previousNum === 0) {
      // If previous is 0 and current is also 0, no change
      if (currentNum === 0) {
        return 0;
      }
      // If previous is 0 but current has value, show as 0% to avoid misleading infinity
      // The actual increase is infinite, but displaying it as 0% is safer
      return 0;
    }
    
    // Calculate percentage change: ((new - old) / old) * 100
    const change = ((currentNum - previousNum) / previousNum) * 100;
    
    // Round to 1 decimal place for cleaner display
    const roundedChange = Math.round(change * 10) / 10;
    
    // Cap extremely high percentages to prevent misleading displays
    // If the percentage is over 500%, it's likely a data issue or needs special handling
    if (Math.abs(roundedChange) > 500) {
      // Still show the direction but cap the value
      return roundedChange > 0 ? 500 : -500;
    }
    
    return roundedChange;
  };

  const trend = currentMetricData ? calculateTrend(currentMetricData.current, currentMetricData.previous) : 0;

  const periodLabel = useMemo(() => {
    const found = periodOptions.find((option) => option.value === selectedPeriod);
    return found ? found.label : selectedPeriod;
  }, [periodOptions, selectedPeriod]);

  const metricLabel = useMemo(() => {
    const found = metricOptions.find((option) => option.value === selectedMetric);
    return found ? found.label.toLowerCase() : selectedMetric;
  }, [metricOptions, selectedMetric]);

  return (
    <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg shadow-lg p-4 border border-gray-700">
      <h3 className="text-lg font-semibold text-white mb-4">{t('dashboard.comparative.title')}</h3>

      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <MetricSelector
          options={metricOptions}
          selectedMetric={selectedMetric}
          onChange={setSelectedMetric}
          label={t('dashboard.comparative.metric')}
        />

        <TimeComparisonFilter
          selectedPeriod={selectedPeriod}
          onChange={setSelectedPeriod}
          options={periodOptions}
          label={t('dashboard.comparative.compareWith')}
        />
      </div>

      {loading ? (
        <div className="w-full h-32 bg-gray-700 animate-pulse rounded-lg" />
      ) : error ? (
        <div className="w-full h-32 flex items-center justify-center">
          <div className="text-gray-400 text-center">
            <p className="text-lg mb-2">{t('dashboard.comparative.errorTitle')}</p>
            <p className="text-sm">{t('dashboard.comparative.errorDescription')}</p>
          </div>
        </div>
      ) : !chartData.length ? (
        <div className="w-full h-32 flex items-center justify-center">
          <div className="text-gray-400 text-center">
            <p className="text-lg mb-2">{t('dashboard.comparative.noDataTitle')}</p>
            <p className="text-sm">{t('dashboard.comparative.noDataDescription')}</p>
          </div>
        </div>
      ) : (
        <div className="w-full p-2">
          <BarDisplay
            data={chartData}
            selectedMetric={selectedMetric}
            formatValue={formatValue}
            labels={{
              current: t('dashboard.comparative.current'),
              previous: t('dashboard.comparative.previous')
            }}
          />
          <div className="mt-4 flex justify-center">
            {renderTrendIndicator(trend)}
          </div>
        </div>
      )}

      <div className="mt-4 text-xs text-gray-400 text-center">
        {t('dashboard.comparative.footnote', {
          metricLabel,
          periodLabel
        })}
      </div>
    </div>
  );
};

export default ComparativeMetrics;